import type { DeviceModel, PoEStandard, PowerInput, PowerInputKind } from '../../types/deviceModel';
import { derivedPsuOutput, isFiberKind, isPowerKind, isBatPortKind, isPvPortKind, POE_STANDARDS, selfDrawWattsAtVoltage, typicalVoltageOfInput, activePowerInputs, inputCapacityWatts, inputCapacityLimit, selfDrawLimitOf } from '../../types/deviceModel';
import type { PortRect } from '../../types';
import { collectModelPorts } from './layoutPorts';

export interface DeviceModelValidation {
  ok: boolean;
  errors: string[];
  warnings: string[];
}

function hasPoeInOnPorts(model: DeviceModel): boolean {
  return collectModelPorts(model).some((p) => !!p.poeIn && p.poeIn.standards.length > 0);
}

function hasPoeOutOnPorts(model: DeviceModel): boolean {
  return collectModelPorts(model).some((p) => !!p.poeOut && p.poeOut.standards.length > 0);
}

function dcOrAcInputs(inputs: PowerInput[]): PowerInput[] {
  return inputs.filter((i) => i.kind === 'ac' || i.kind === 'dc-jack' || i.kind === 'dc-terminal');
}

function inputCanProduceVoltage(inputs: PowerInput[], voltage: number): boolean {
  return inputs.some((input) => {
    if (input.kind === 'ac') return true;
    const min = input.voltageMin ?? 0;
    const max = input.voltageMax ?? 999;
    return voltage >= min && voltage <= max;
  });
}

export function validateDeviceModel(model: DeviceModel): DeviceModelValidation {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!model || typeof model !== 'object') {
    return { ok: false, errors: ['Device model is missing.'], warnings: [] };
  }

  if (selfDrawLimitOf(model) === 'amps') {
    const amps = model.selfDrawAmps ?? 0;
    if (typeof amps !== 'number' || Number.isNaN(amps) || amps < 0) {
    errors.push('Self consumption (amps) must be 0 or greater.');
    }
  } else if (typeof model.selfDrawWatts !== 'number' || Number.isNaN(model.selfDrawWatts) || model.selfDrawWatts < 0) {
    errors.push('Self consumption (watts) is required and must be 0 or greater.');
  }

  const inputs = Array.isArray(model.powerInputs) ? model.powerInputs : [];
  const ports = collectModelPorts(model);

  if (ports.length === 0 && !model.isPsu) {
    errors.push('Add at least one port in the layout.');
  }

  const canLive =
    model.isPassthroughDevice ||
    model.isPsu ||
    model.isSolar ||
    model.isBattery ||
    model.isInverter ||
    inputs.length > 0 ||
    hasPoeInOnPorts(model);

  if (!canLive) {
    errors.push('This device has no way to power on. Add AC, DC, or PoE-in, or mark it as a patch panel / PSU.');
  }

  inputs.forEach((input) => {
    if (input.kind === 'dc-jack' || input.kind === 'dc-terminal') {
      if (input.voltageMin == null || input.voltageMax == null) {
        errors.push(`${input.kind} needs a voltage range.`);
      } else if (input.voltageMin > input.voltageMax) {
        errors.push(`${input.kind} minimum voltage cannot be above maximum.`);
      }
      if (inputCapacityLimit(input) === 'amps') {
        if (input.maxAmps == null || input.maxAmps <= 0) {
          errors.push(`${input.kind} needs a maximum amperage.`);
        }
      } else if (input.maxWatts == null || input.maxWatts <= 0) {
        errors.push(`${input.kind} needs a maximum wattage.`);
      }
    }
    if (input.kind === 'poe-in' && (input.portNumber == null || input.portNumber < 1)) {
      errors.push('PoE-in must be bound to a real layout port number.');
    }
    if (input.kind === 'pv') {
      if (input.voltageMax == null || !(input.voltageMax > 0)) errors.push('PV / MPPT input needs a maximum voltage (Voc limit).');
      if (input.mpptVoltageMin == null || input.mpptVoltageMax == null) {
        errors.push('PV / MPPT input needs an MPPT voltage window.');
      } else if (input.mpptVoltageMin > input.mpptVoltageMax) {
        errors.push('MPPT minimum voltage cannot be above maximum.');
      }
      if (input.maxWatts == null || !(input.maxWatts > 0)) errors.push('PV / MPPT input needs a maximum array wattage.');
      if (input.plusPortNumber == null || input.minusPortNumber == null) {
        errors.push('PV / MPPT input must bind a PV+ and PV− layout port.');
      }
    }
    if (input.kind === 'battery') {
      if (input.voltageMin == null || input.voltageMax == null) errors.push('Battery input needs a voltage range.');
      if (input.plusPortNumber == null || input.minusPortNumber == null) {
        errors.push('Battery input must bind BAT+ and BAT− layout ports.');
      }
    }
  });

  if (hasPoeOutOnPorts(model) && dcOrAcInputs(inputs).length === 0 && !inputs.some((i) => i.kind === 'poe-in')) {
    errors.push('PoE-out requires a power input (AC, DC, or PoE-in) that can feed it.');
  }

  const maxInputWatts = Math.max(
    0,
    ...activePowerInputs(inputs).map((i) => inputCapacityWatts(i) ?? 0),
    ...ports.filter((p) => p.poeIn?.maxWatts).map((p) => p.poeIn!.maxWatts as number)
  );
  const peakV = Math.max(0, ...activePowerInputs(inputs).map((i) => i.voltageMax ?? typicalVoltageOfInput(i) ?? 0));
  const peakDraw = selfDrawWattsAtVoltage(model, peakV || undefined);
  if (peakDraw > 0 && maxInputWatts > 0 && peakDraw >= maxInputWatts && !model.isPassthroughDevice) {
    warnings.push('Self consumption is at or above the maximum input watts; PoE-out will have no budget.');
  }

  const seen = new Set<string>();
  ports.forEach((port) => {
    const key = `${port.face}:${port.number}:${port.kind}`;
    if (seen.has(key)) {
      errors.push(`Duplicate port ${port.label || port.number} (${port.kind}) on ${port.face}.`);
    }
    seen.add(key);

    const kind = String(port.kind || '');
    if (isFiberKind(kind) && (port.poeIn || port.poeOut)) {
      errors.push(`${port.label || `Port ${port.number}`} is ${kind} and cannot have PoE.`);
    }
    if (isPowerKind(kind) && (port.poeIn || port.poeOut || port.passthrough)) {
      errors.push(`${port.label || `Port ${port.number}`} is a power port and cannot carry data/PoE.`);
    }
    if (port.poeIn) {
      if (!port.poeIn.standards.length) errors.push(`${port.label || `Port ${port.number}`} PoE-in needs at least one standard.`);
      if (port.poeIn.voltageMin > port.poeIn.voltageMax) {
        errors.push(`${port.label || `Port ${port.number}`} PoE-in voltage range is inverted.`);
      }
    }
    if (port.poeOut) {
      if (!port.poeOut.standards.length) errors.push(`${port.label || `Port ${port.number}`} PoE-out needs at least one standard.`);
      if (!(port.poeOut.perPortMaxWatts > 0)) {
        errors.push(`${port.label || `Port ${port.number}`} PoE-out needs a per-port watt limit.`);
      }
      if (!port.poeOut.outputFollowsInputVoltage) {
        port.poeOut.standards.forEach((std) => {
          const v = std.startsWith('passive-24') ? 24 : 48;
          if (dcOrAcInputs(inputs).length > 0 && !inputCanProduceVoltage(inputs, v) && !inputs.some((i) => i.kind === 'poe-in')) {
            errors.push(`${port.label || `Port ${port.number}`} cannot offer ${std} with the current power inputs.`);
          }
        });
      }
    }
  });

  if (model.isPsu && !derivedPsuOutput(model)) {
    errors.push('A PSU needs an output voltage and wattage.');
  }

  if (model.isSolar) {
    if (!(model.pvVoc! > 0) || !(model.pvVmp! > 0)) errors.push('A solar panel needs Voc and Vmp.');
    if (!(model.pvStcWatts! > 0) && !(model.pvIsc! > 0)) errors.push('A solar panel needs STC watts (or Isc).');
    if (!ports.some((p) => isPvPortKind(String(p.kind)) && String(p.kind) === 'pvPlus') || !ports.some((p) => String(p.kind) === 'pvMinus')) {
      errors.push('A solar panel needs PV+ and PV− ports.');
    }
  }
  if (model.isBattery) {
    if (!(model.capacityAh! > 0) || !(model.nominalVoltage! > 0)) errors.push('A battery needs Ah and nominal voltage.');
    if (!ports.some((p) => String(p.kind) === 'batPlus') || !ports.some((p) => String(p.kind) === 'batMinus')) {
      errors.push('A battery needs BAT+ and BAT− ports.');
    }
  }
  if (model.isInverter) {
    const hasPv = inputs.some((i) => i.kind === 'pv');
    const hasBat = inputs.some((i) => i.kind === 'battery') || (ports.some((p) => isBatPortKind(String(p.kind))));
    const hasAcOut = (model.powerOutputs || []).some((o) => o.kind === 'ac' && o.enabled !== false);
    if (!hasPv && !hasBat) errors.push('An inverter needs a PV/MPPT input and/or a battery input.');
    if (!hasAcOut) errors.push('An inverter needs an AC output.');
  }

  return { ok: errors.length === 0, errors, warnings };
}

export function poeStandardsProduceableByInputs(
  inputs: PowerInput[],
  followsInputVoltage: boolean
): PoEStandard[] {
  if (!inputs.length) return [];
  if (followsInputVoltage) {
    const min = Math.min(...inputs.map((i) => i.voltageMin ?? (i.kind === 'ac' ? 48 : 0)));
    const max = Math.max(...inputs.map((i) => i.voltageMax ?? (i.kind === 'ac' ? 57 : 0)));
    const out: PoEStandard[] = [];
    if (min <= 24 && max >= 24) out.push('passive-24');
    if (max >= 44) out.push('802.3af', '802.3at', 'passive-48');
    if (max >= 50) out.push('802.3bt');
    return Array.from(new Set(out));
  }
  return POE_STANDARDS.filter((std) => {
    const v = std === 'passive-24' ? 24 : 48;
    return inputCanProduceVoltage(inputs, v);
  });
}

export function stripIllegalPortLogic(port: PortRect, kind: string = String(port.kind)): PortRect {
  const next: PortRect = { ...port, kind: kind as PortRect['kind'] };
  if (isFiberKind(kind) || isPowerKind(kind) || kind.startsWith('custom:') || kind === 'usb' || kind === 'console' || kind === 'hdmi' || kind === 'audio' || kind === 'coax') {
    delete next.poeIn;
    delete next.poeOut;
    delete next.poeInRoleId;
    delete next.poeOutRoleId;
  }
  if (isFiberKind(kind)) {
    delete next.passthrough;
  }
  if (isPowerKind(kind)) {
    delete next.speedsMbps;
    delete next.passthrough;
    delete next.comboGroup;
  }
  if (kind !== 'rj45') {
    if (!isCopperKindish(kind)) {
      delete next.poeIn;
      delete next.poeOut;
      delete next.poeInRoleId;
      delete next.poeOutRoleId;
    }
  }
  return next;
}

function isCopperKindish(kind: string): boolean {
  return kind === 'rj45' || kind === 'ethernet';
}

export function defaultLogicForKind(kind: string): Partial<PortRect> {
  if (kind === 'rj45') return { speedsMbps: [10, 100, 1000] };
  if (kind === 'sfp') return { speedsMbps: [1000] };
  if (kind === 'sfpPlus') return { speedsMbps: [1000, 10000] };
  return {};
}

export function applyPortKindChange(port: PortRect, newKind: string): PortRect {
  const base = stripIllegalPortLogic({ ...port, ...defaultLogicForKind(newKind) }, newKind);
  return { ...base, kind: newKind as PortRect['kind'] };
}

export function powerInputKindsOnModel(model: DeviceModel): PowerInputKind[] {
  return Array.from(new Set(model.powerInputs.map((i) => i.kind)));
}
